import { lazy, Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const PipelineRunsPage = lazy(() => import('./pages/PipelineRunsPage'));
const ModelRegistryPage = lazy(() => import('./pages/ModelRegistryPage'));
const DatasetsPage = lazy(() => import('./pages/DatasetsPage'));
const QualityGatesPage = lazy(() => import('./pages/QualityGatesPage'));
const FeedbackLoopsPage = lazy(() => import('./pages/FeedbackLoopsPage'));
const CorrectionsPage = lazy(() => import('./pages/CorrectionsPage'));
const SignalsPage = lazy(() => import('./pages/SignalsPage'));
const EvalResultsPage = lazy(() => import('./pages/EvalResultsPage'));
const EvalRunDetailPage = lazy(() => import('./pages/EvalRunDetailPage'));
const EvalQuestionDetailPage = lazy(() => import('./pages/EvalQuestionDetailPage'));
const CorpusHealthPage = lazy(() => import('./pages/CorpusHealthPage'));
const RegulatoryFeedsPage = lazy(() => import('./pages/RegulatoryFeedsPage'));
const PresenceFleetPage = lazy(() => import('./pages/PresenceFleetPage'));
// Sessions page (formerly SessionsPage)
const TaskSchedulerPage = lazy(() => import('../ops/pages/TaskSchedulerPage'));
const AgentFleetPage = lazy(() => import('./pages/AgentFleetPage'));
const AgentDetailPage = lazy(() => import('./pages/AgentDetailPage'));
const PlaybooksPage = lazy(() => import('./pages/PlaybooksPage'));
const TriggersPage = lazy(() => import('../ops/pages/TriggersPage'));
const ObservabilityPage = lazy(() => import('./pages/ObservabilityPage'));
const LlmLogsPage = lazy(() => import('./pages/LlmLogsPage'));
const AuditTrailPage = lazy(() => import('./pages/AuditTrailPage'));
const SessionsPage = lazy(() => import('./pages/SessionsPage'));

const PageSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="flex items-center gap-3 text-neutral-400 animate-pulse">
        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        Loading...
      </div>
    </div>
  }>
    {children}
  </Suspense>
);

const CoordinationPage = lazy(() => import('./pages/CoordinationPage'));
const AgentCatalogPage = lazy(() => import('./pages/AgentCatalogPage'));
const StagingPage = lazy(() => import('./pages/StagingPage'));
const DemoEnvironmentsPage = lazy(() => import('./pages/DemoEnvironmentsPage'));
const DemoControlPanel = lazy(() => import('./pages/DemoControlPanel'));
const SkillsPage = lazy(() => import('./pages/SkillsPage'));
const FirmKnowledgePage = lazy(() => import('./pages/FirmKnowledgePage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const DesignStudioPage = lazy(() => import('./pages/DesignStudioPage'));
const TopicPrototypePage = lazy(() => import('./pages/TopicPrototypePage'));
const MarthaMindPage = lazy(() => import('./pages/MarthaMindPage'));
const AnonymiserRedirect = () => {
  const { wsSlug } = useWorkspace();
  return <Navigate to={`/w/${wsSlug ?? 'archie'}/success/anonymiser`} replace />;
};
const PlaygroundIndexPage = lazy(() => import('./pages/playground/PlaygroundPage').then(m => ({ default: m.PlaygroundIndexPage })));
const PlaygroundPage = lazy(() => import('./pages/playground/PlaygroundPage').then(m => ({ default: m.PlaygroundPage })));

// Development Hub (monitor + clients)
const DevelopmentPage = lazy(() => import('./pages/development/DevelopmentPage'));

// Archie Connect ops pane
const ConnectOrgsPage = lazy(() => import('./pages/connect/ConnectOrgsPage'));
const ConnectEventsPage = lazy(() => import('./pages/connect/ConnectEventsPage'));
const ConnectKeysPage = lazy(() => import('./pages/connect/KeysPage'));
const ConnectUsagePage = lazy(() => import('./pages/connect/UsagePage'));
const ConnectEndpointRegistryPage = lazy(() => import('./pages/connect/EndpointRegistryPage'));

/**
 * Archie route children — returns <Route> elements as a fragment.
 *
 * These are composed into the parent <Routes> in App.tsx as children
 * of the Archie layout route. NO nested <Routes> — the parent router
 * has full visibility for correct route ranking.
 */
export function ArchieRouteChildren() {
  return (
    <>
      {/* Default redirect to pipeline runs */}
      <Route index element={<Navigate to="pipelines" replace />} />

      {/* TRAINING */}
      <Route path="pipelines" element={<PageSuspense><PipelineRunsPage /></PageSuspense>} />
      <Route path="models" element={<PageSuspense><ModelRegistryPage /></PageSuspense>} />
      <Route path="datasets" element={<PageSuspense><DatasetsPage /></PageSuspense>} />
      <Route path="quality-gates" element={<PageSuspense><QualityGatesPage /></PageSuspense>} />
      <Route path="evals" element={<PageSuspense><EvalResultsPage /></PageSuspense>} />
      <Route path="evals/:runId/q/:questionId" element={<PageSuspense><EvalQuestionDetailPage /></PageSuspense>} />
      <Route path="evals/:runId" element={<PageSuspense><EvalRunDetailPage /></PageSuspense>} />

      {/* LEARNING */}
      <Route path="feedback" element={<PageSuspense><FeedbackLoopsPage /></PageSuspense>} />
      <Route path="corrections" element={<PageSuspense><CorrectionsPage /></PageSuspense>} />
      <Route path="signals" element={<PageSuspense><SignalsPage /></PageSuspense>} />

      {/* QUALITY DASHBOARD — Corpus Health */}
      <Route path="corpus-health" element={<PageSuspense><CorpusHealthPage /></PageSuspense>} />

      {/* AGENTS — Sessions is the unified page (conversations + playbook sessions) */}
      <Route path="sessions/:conversationId/*" element={<PageSuspense><SessionsPage /></PageSuspense>} />
      <Route path="sessions" element={<PageSuspense><SessionsPage /></PageSuspense>} />
      <Route path="fleet/:agentId" element={<PageSuspense><AgentDetailPage /></PageSuspense>} />
      <Route path="fleet" element={<PageSuspense><AgentFleetPage /></PageSuspense>} />
      <Route path="playbooks" element={<PageSuspense><PlaybooksPage /></PageSuspense>} />
      <Route path="triggers" element={<PageSuspense><TriggersPage /></PageSuspense>} />

      {/* Coordination */}
      <Route path="coordination" element={<PageSuspense><CoordinationPage /></PageSuspense>} />

      {/* Agent Catalog */}
      <Route path="catalog/:slug" element={<PageSuspense><AgentCatalogPage /></PageSuspense>} />
      <Route path="catalog" element={<PageSuspense><AgentCatalogPage /></PageSuspense>} />

      {/* OBSERVABILITY */}
      <Route path="dashboard" element={<PageSuspense><ObservabilityPage /></PageSuspense>} />
      <Route path="logs" element={<PageSuspense><LlmLogsPage /></PageSuspense>} />
      <Route path="audit" element={<PageSuspense><AuditTrailPage /></PageSuspense>} />

      {/* SKILLS */}
      <Route path="skills" element={<PageSuspense><SkillsPage /></PageSuspense>} />

      {/* KNOWLEDGE */}
      <Route path="firm-knowledge" element={<PageSuspense><FirmKnowledgePage /></PageSuspense>} />
      <Route path="clients" element={<PageSuspense><ClientsPage /></PageSuspense>} />

      {/* INFRASTRUCTURE */}
      <Route path="regulatory-feeds" element={<PageSuspense><RegulatoryFeedsPage /></PageSuspense>} />
      <Route path="scheduler" element={<PageSuspense><TaskSchedulerPage /></PageSuspense>} />
      <Route path="presence" element={<PageSuspense><PresenceFleetPage /></PageSuspense>} />

      {/* STAGING */}
      <Route path="staging" element={<PageSuspense><StagingPage /></PageSuspense>} />

      {/* DEMO ENVIRONMENTS */}
      <Route path="demo" element={<PageSuspense><DemoEnvironmentsPage /></PageSuspense>} />
      <Route path="demo/:envId" element={<PageSuspense><DemoControlPanel /></PageSuspense>} />

      {/* DESIGN STUDIO */}
      <Route path="design-studio" element={<PageSuspense><DesignStudioPage /></PageSuspense>} />

      {/* TOPIC PROTOTYPE */}
      <Route path="topic-prototype" element={<PageSuspense><TopicPrototypePage /></PageSuspense>} />

      {/* MARTHA MIND — Always-On Agent Dashboard */}
      <Route path="martha-mind" element={<PageSuspense><MarthaMindPage /></PageSuspense>} />

      {/* ANONYMISER — moved to S&S pane */}
      <Route path="anonymiser" element={<AnonymiserRedirect />} />

      {/* PLAYGROUND */}
      <Route path="playground" element={<PageSuspense><PlaygroundIndexPage /></PageSuspense>} />
      <Route path="playground/:pageId" element={<PageSuspense><PlaygroundPage /></PageSuspense>} />

      {/* DEVELOPMENT HUB — temporal health, PR board, telemetry, MTD clients */}
      <Route path="development" element={<PageSuspense><DevelopmentPage /></PageSuspense>} />
      <Route path="monitor" element={<Navigate to="../development" replace />} />
      <Route path="mo" element={<Navigate to="../development" replace />} />

      {/* ARCHIE CONNECT — developer platform ops */}
      <Route path="connect" element={<Navigate to="connect/orgs" replace />} />
      <Route path="connect/orgs" element={<PageSuspense><ConnectOrgsPage /></PageSuspense>} />
      <Route path="connect/events" element={<PageSuspense><ConnectEventsPage /></PageSuspense>} />
      <Route path="connect/keys" element={<PageSuspense><ConnectKeysPage /></PageSuspense>} />
      <Route path="connect/usage" element={<PageSuspense><ConnectUsagePage /></PageSuspense>} />
      <Route path="connect/endpoints" element={<PageSuspense><ConnectEndpointRegistryPage /></PageSuspense>} />

      {/* Legacy 2-letter code redirects */}
      <Route path="pi" element={<Navigate to="../pipelines" replace />} />
      <Route path="mr" element={<Navigate to="../models" replace />} />
      <Route path="ds" element={<Navigate to="../datasets" replace />} />
      <Route path="qg" element={<Navigate to="../quality-gates" replace />} />
      <Route path="ev" element={<Navigate to="../evals" replace />} />
      <Route path="ev/:runId" element={<Navigate to="../evals" replace />} />
      <Route path="fl" element={<Navigate to="../feedback" replace />} />
      <Route path="cw" element={<Navigate to="../corrections" replace />} />
      <Route path="is" element={<Navigate to="../signals" replace />} />
      <Route path="ch" element={<Navigate to="../corpus-health" replace />} />
      <Route path="se" element={<Navigate to="../sessions" replace />} />
      <Route path="af" element={<Navigate to="../fleet" replace />} />
      <Route path="pb" element={<Navigate to="../playbooks" replace />} />
      <Route path="tc" element={<Navigate to="../triggers" replace />} />
      <Route path="co" element={<Navigate to="../coordination" replace />} />
      <Route path="ac" element={<Navigate to="../catalog" replace />} />
      <Route path="ob" element={<Navigate to="../dashboard" replace />} />
      <Route path="lg" element={<Navigate to="../logs" replace />} />
      <Route path="at" element={<Navigate to="../audit" replace />} />
      <Route path="sk" element={<Navigate to="../skills" replace />} />
      <Route path="kf" element={<Navigate to="../firm-knowledge" replace />} />
      <Route path="kc" element={<Navigate to="../clients" replace />} />
      <Route path="rf" element={<Navigate to="../regulatory-feeds" replace />} />
      <Route path="rt" element={<Navigate to="../scheduler" replace />} />
      <Route path="pf" element={<Navigate to="../presence" replace />} />
      <Route path="sg" element={<Navigate to="../staging" replace />} />
      <Route path="de" element={<Navigate to="../demo" replace />} />
      <Route path="dg" element={<Navigate to="../design-studio" replace />} />
      <Route path="tp" element={<Navigate to="../topic-prototype" replace />} />
      <Route path="mm" element={<Navigate to="../martha-mind" replace />} />
      <Route path="pg" element={<Navigate to="../playground" replace />} />
      <Route path="cn" element={<Navigate to="../connect/orgs" replace />} />
      <Route path="cn-ky" element={<Navigate to="../connect/keys" replace />} />
      <Route path="cn-us" element={<Navigate to="../connect/usage" replace />} />
      <Route path="cn-ep" element={<Navigate to="../connect/endpoints" replace />} />

      {/* No catch-all — unmatched routes stay where they are instead of infinite redirect loops */}
    </>
  );
}
